import React, { useEffect, useMemo, useState } from 'react';
import DialogBox from './DialogBox';
import useFetchData from '../helpers/useFetch';
import AppURL from './AppURL';
import { useFormik } from 'formik';
import { fetchDataFromApi } from '../helpers/api';
import { ToastSuccess } from './Toast';
import { Grid } from '@mui/material';
import InputTextBox from './InputTextBox';
import SelectInputBox from './SelectInputBox';
import { handleSelectBox } from '../helpers/handleChange';
import { ToleOptions } from './Options';
import { EndBaseLine } from './AppSpace';
import { Button } from './Button';
import AppTable from './AppTable';
import { SearchMemberList } from '../helpers/models/Models';
import Container from './Container';
import { useDispatch } from 'react-redux';
import { SOFTWARE_NAME_ORG } from '../store/orgSlice';

interface SearchMemberListViewProps {
  openSearch: boolean;
  setOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
}

const SearchMemberListView: React.FC<SearchMemberListViewProps> = ({ openSearch, setOpenSearch }) => {
  const { data, loading, error } = useFetchData(AppURL.ApplicationURL + 'app/search/member/options/list/view/');
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(SOFTWARE_NAME_ORG(data?.org));
  }, [data]);
  const [onMemData, setOnMemList] = useState<SearchMemberList[]>([]);
  // Define columns based on the new data structure
  const columns = useMemo(() => [
    { header: 'Member SN', accessor: 'member_sn', sortable: false },
    { header: 'Member Name', accessor: 'mem_name', sortable: true },
    { header: 'Address', accessor: 'add_eng', sortable: false },
    { header: 'Phone No', accessor: 'phone_no', sortable: false },
    { header: 'Tap Size Name', accessor: 'tap_size_name', sortable: false },
    { header: 'Reader', accessor: 'reader_name', sortable: false },
    { header: 'Spouse Name', accessor: 'spouse_name', sortable: false },

  ], []);

  // Map data to match the table columns
  const modifiedData = useMemo(() => {
    if (!onMemData || !onMemData.length) return [];
    return onMemData.map((item: SearchMemberList) => ({
      member_sn: item.member_sn,
      mem_name: item.mem_name,
      phone_no: item.phone_no || 'N/A',
      add_eng: item.add_eng || 'N/A',
    }));
  }, [onMemData]);
  const margaToleOptions = ToleOptions(data?.data);
  const initialValues = {
    marga_tole: "",
    member_sn: "",
    member_data: "",
  };
  const { values, errors, touched, setFieldValue, isSubmitting, handleBlur, handleChange, handleSubmit } = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values, action) => {
      const queryParams = new URLSearchParams({
        marga_tole: values.marga_tole,
        member_sn: values.member_sn,
        member_data: values.member_data,
      });
      try {
        await fetchDataFromApi(AppURL.ApplicationURL + "app/product/member/search/report/list/", queryParams).then((res) => {
          setOnMemList(res.results);
          if (res?.status === 201) {
            ToastSuccess({ message: res?.message });
            action.resetForm();
          }
          action.resetForm();
        });
      } catch (error) {
        console.log(error);
      }
    },
  });

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <DialogBox headerName='Search Member' className='!w-4/5' open={openSearch} close={() => setOpenSearch(false)}>
      <form onSubmit={handleSubmit} className='my-3'>
        <Grid container spacing={0.5}>
          <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
            <SelectInputBox
              label="Marga/Tole"
              options={margaToleOptions}
              name='marga_tole'
              required={false}
              optionSelect={(name, selectedOption) => handleSelectBox({ setFieldValue, name, value: selectedOption })}
              placeholder='Select Marga/Tole'
              value={values.marga_tole}
              touched={touched.marga_tole}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} xl={6} sm={12}>
            <InputTextBox
              type='number'
              label='Member SN'
              name='member_sn'
              handleChange={handleChange}
              handleBlur={handleBlur}
              disabled={false}
              value={values.member_sn}
              placeholder='10'
              required={false}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} xl={6} sm={12}>
            <InputTextBox
              type='text'
              label='Member Name/Phone No'
              name='member_data'
              handleChange={handleChange}
              handleBlur={handleBlur}
              disabled={false}
              value={values.member_data}
              placeholder='Enter name or phone no'
              required={false}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} xl={6} sm={12}>
            <EndBaseLine top={20}>
              <Button btn_type='search' type='submit' el='button' />
            </EndBaseLine>
          </Grid>
        </Grid>
      </form>
      {/* Custom Table to Display the Member List using HTML and Tailwind CSS */}
      <Container>
        <div className="overflow-x-auto text-nowrap">
          <table className="min-w-full table-auto border-collapse text-sm text-left text-gray-700">
            <thead className="bg-gray-100">
              <tr>
                {columns.map((column) => (
                  <th key={column.accessor} className="px-4 py-1 border-b font-semibold text-gray-600">
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {onMemData.length > 0 ? (
                onMemData.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-1 border-b">
                      {row.member_sn}
                    </td>
                    <td className="px-4 py-1 border-b">
                      {row.mem_name}
                    </td>
                    <td className="px-4 py-1 border-b">
                      {row.add_eng}
                    </td>
                    <td className="px-4 py-1 border-b">
                      {row.phone_no}
                    </td>
                    <td className="px-4 py-1 border-b">
                      {row.tap_size_name}
                    </td>
                    <td className="px-4 py-1 border-b">
                      {row.reader_name}
                    </td>
                    <td className="px-4 py-1 border-b">
                      {row.spouse_name}
                    </td>
                    
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-1 text-center text-gray-500">
                    No data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Container>
    </DialogBox>
  );
}

export default SearchMemberListView;
